import { ArrowRightCircle } from '@tamagui/lucide-icons';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Paragraph } from '@unistocks/components/text/text';
import { usePortfolioValue } from '@unistocks/features/portfolios/hooks/use-portfolio-values';
import { AssetCollection, BucketDto, PortfolioDto } from '@unistocks/proxy/axios-client';
import { formatCurrency, formatPercentage, t } from '@unistocks/shared/localization/index.ts';
import { createDate, isDefined, isNumber } from '@unistocks/shared/utils/util';
import { Filter } from '@unistocks/types';
import { memo, useMemo } from 'react';
import { isWeb, Separator, Spinner, styled, ViewProps } from 'tamagui';

export type PortfolioValueSummaryProps = ViewProps & {
  portfolio: PortfolioDto;
  todaysChange?: BucketDto;
  isTodaysChangeFetching?: boolean;
  assetFilter: Filter<string>;
  sectionFilter: Filter<string>;
};

export const PortfolioValueSummary: React.FC<PortfolioValueSummaryProps> = memo(
  ({ portfolio, todaysChange, isTodaysChangeFetching, assetFilter, sectionFilter, ...props }) => {
    const { nominalChange, percentageChange } = useMemo(() => {
      if (!todaysChange) return {};

      const startValue = todaysChange?.entries[0];
      const endValue = todaysChange?.entries.at(-1);

      if (!startValue || !endValue) return {};

      const startDate = createDate(startValue.timestamp);
      const endDate = createDate(endValue.timestamp);

      if (endDate.diff(startDate, 'days').days !== 1) return {};

      const percentageChange = endValue.value / startValue.value - 1 || 0;
      const nominalChange =
        endValue.value * startValue.shares - startValue.value * startValue.shares;

      return {
        percentageChange,
        nominalChange
      };
    }, [todaysChange]);

    const totalValue = usePortfolioValue(portfolio, {
      type: AssetCollection.All,
      sections: sectionFilter,
      assets: assetFilter
    });
    const holdingsValue = usePortfolioValue(portfolio, {
      type: AssetCollection.Holdings,
      sections: sectionFilter,
      assets: assetFilter
    });
    const cashValue = usePortfolioValue(portfolio, {
      type: AssetCollection.Cash,
      sections: sectionFilter,
      assets: assetFilter
    });
    const loanValue = usePortfolioValue(portfolio, {
      type: AssetCollection.Loans,
      sections: sectionFilter,
      assets: assetFilter
    });

    const todaysChangeColor =
      isTodaysChangeFetching || !isDefined(percentageChange)
        ? '$gray8'
        : percentageChange >= 0
          ? '$positive'
          : '$negative';

    return (
      <Column {...props}>
        <SummaryValueRow>
          <Paragraph fontWeight={'$7'}>{t('::Holdings')}</Paragraph>
          <Paragraph fontWeight={'$7'}>
            {formatCurrency(holdingsValue!, portfolio.currency)}
          </Paragraph>
        </SummaryValueRow>

        <SummaryValueRow mt={'$1'}>
          <Paragraph fontWeight={'$7'}>{t('::CashHoldings')}</Paragraph>
          <Paragraph fontWeight={'$7'}>{formatCurrency(cashValue!, portfolio.currency)}</Paragraph>
        </SummaryValueRow>

        {loanValue > 0 ? (
          <SummaryValueRow mt={'$1'}>
            <Paragraph fontWeight={'$7'}>{t('::LoanHoldings')}</Paragraph>
            <Paragraph fontWeight={'$7'}>
              {formatCurrency(loanValue!, portfolio.currency)}
            </Paragraph>
          </SummaryValueRow>
        ) : null}

        <Separator />

        <SummaryValueRow mt={'$2'}>
          <Paragraph size={'$6'} fontWeight={'$7'}>
            {t('::TotalValue')}
          </Paragraph>
          <Paragraph size={'$6'} fontWeight={'$7'}>
            {formatCurrency(totalValue!, portfolio.currency)}
          </Paragraph>
        </SummaryValueRow>

        {todaysChange || isTodaysChangeFetching ? (
          <>
            <SummaryValueRow mt={'$2'}>
              <Paragraph fontWeight={'$7'}>{t('::TodaysReturns')}</Paragraph>

              <Row alignItems="center">
                {percentageChange ? (
                  <ArrowRightCircle
                    size={24}
                    color={todaysChangeColor}
                    rotate={percentageChange > 0 ? '-45deg' : '45deg'}
                  />
                ) : null}
                {isNumber(percentageChange) ? (
                  <Paragraph size={'$8'} color={todaysChangeColor}>
                    {formatPercentage(percentageChange, {
                      signDisplay: isWeb ? 'negative' : 'exceptZero'
                    })}
                  </Paragraph>
                ) : null}
                {isTodaysChangeFetching ? <Spinner scale={0.75} color={'$gray9'} /> : null}
              </Row>
            </SummaryValueRow>

            <SummaryValueRow>
              <Paragraph>{''}</Paragraph>
              {isNumber(nominalChange) ? (
                <Paragraph fontWeight={'$7'} color={todaysChangeColor} ml={'auto'}>
                  {formatCurrency(nominalChange, portfolio.currency, { signDisplay: 'exceptZero' })}
                </Paragraph>
              ) : null}
              {isTodaysChangeFetching ? <Spinner scale={0.75} color={'$gray9'} /> : null}
            </SummaryValueRow>
          </>
        ) : null}
      </Column>
    );
  }
);

PortfolioValueSummary.displayName = 'PortfolioValueSummary';

const SummaryValueRow = styled(Row, {
  name: 'SummaryValueRow',
  justifyContent: 'space-between',
  gap: '$1'
});
